import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, HeartHandshake, Sparkles, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const emojiFor = (v) => {
  if (v <= 2) return "😞";
  if (v <= 4) return "😐";
  if (v <= 6) return "🙂";
  if (v <= 8) return "😊";
  return "🤩";
};

export default function AppSatisfactionStep({ sessionNumber, onDone }) {
  const [satisfaction, setSatisfaction] = useState(8);
  const [helpfulness, setHelpfulness] = useState(8);
  const [qualitativeFeedback, setQualitativeFeedback] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const trimmedFeedback = qualitativeFeedback.trim();
      await api.post("/progress", {
        session_number: sessionNumber,
        step: "app-satisfaction",
        completed: true,
        payload: { satisfaction, helpfulness, qualitative_feedback: trimmedFeedback },
      });
      toast.success("Gracias por tu valoración 💚");
      onDone({ satisfaction, helpfulness, qualitative_feedback: trimmedFeedback });
    } catch {
      toast.error("No pudimos guardar tu valoración.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="app-satisfaction-step">
      <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">
        Antes de terminar, nos encantaría saber tu opinión sobre MINDHOME HEALTH APP. Tus respuestas son anónimas para el resto de usuarios y nos ayudan a mejorar el programa.
      </p>

      <div className="mt-8 space-y-5">
        <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[var(--ha-secondary)]" strokeWidth={1.75} />
              <p className="font-semibold text-[var(--ha-text)]">¿Cuál es tu grado de satisfacción con MINDHOME HEALTH APP?</p>
            </div>
            <p className="text-xs text-[var(--ha-muted)]">1 = Nada satisfecho/a · 10 = Extremadamente satisfecho/a</p>
            <div className="mt-5 flex items-center gap-5">
              <span className="text-3xl">{emojiFor(satisfaction)}</span>
              <Slider
                data-testid="satisfaction-slider"
                value={[satisfaction]}
                onValueChange={([v]) => setSatisfaction(v)}
                min={1}
                max={10}
                step={1}
                className="flex-1"
              />
              <span className="text-3xl font-extrabold text-[var(--ha-primary)] w-12 text-right" data-testid="satisfaction-value">{satisfaction}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <HeartHandshake className="w-4 h-4 text-[var(--ha-primary)]" strokeWidth={1.75} />
              <p className="font-semibold text-[var(--ha-text)]">¿Cuánto te ha ayudado MINDHOME HEALTH APP?</p>
            </div>
            <p className="text-xs text-[var(--ha-muted)]">1 = Nada me ha ayudado · 10 = Me ha ayudado muchísimo</p>
            <div className="mt-5 flex items-center gap-5">
              <span className="text-3xl">{emojiFor(helpfulness)}</span>
              <Slider
                data-testid="helpfulness-slider"
                value={[helpfulness]}
                onValueChange={([v]) => setHelpfulness(v)}
                min={1}
                max={10}
                step={1}
                className="flex-1"
              />
              <span className="text-3xl font-extrabold text-[var(--ha-primary)] w-12 text-right" data-testid="helpfulness-value">{helpfulness}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-5 rounded-2xl border-[var(--ha-border)] bg-white">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquareHeart className="w-4 h-4 text-[var(--ha-accent)]" strokeWidth={1.75} />
            <p className="font-semibold text-[var(--ha-text)]">¿Qué mejorarías de MINDHOME HEALTH APP?</p>
          </div>
          <p className="text-xs text-[var(--ha-muted)] mb-3">
            Comparte con nosotros lo que más te ha gustado, lo que echaste en falta o cualquier sugerencia. Tu opinión es anónima y nos ayuda a mejorar el programa. (Opcional)
          </p>
          <Textarea
            data-testid="qualitative-feedback-textarea"
            value={qualitativeFeedback}
            onChange={(e) => setQualitativeFeedback(e.target.value.slice(0, 1500))}
            placeholder="Escribe aquí tus ideas, sugerencias o comentarios…"
            className="min-h-[120px] rounded-xl border-[var(--ha-border)] focus-visible:ring-[var(--ha-primary)]"
          />
          <p className="mt-2 text-right text-[10px] text-[var(--ha-muted)]" data-testid="qualitative-feedback-counter">
            {qualitativeFeedback.length}/1500
          </p>
        </CardContent>
      </Card>

      <div className="mt-8 flex justify-end">
        <Button
          data-testid="app-satisfaction-continue"
          onClick={save}
          disabled={saving}
          className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
        >
          Enviar valoración <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
